import {AlertTitle, Container, Stack} from '@mui/material';
import React, {Fragment, useState} from 'react';

import {
	makeDeleteFilePreview,
	makePostUploadFilePreview,
} from 'main/factories/usecases/private/refund/request-preview';
import {AccordionData, AlertInfo, CTAButtonFixed, MainFooter} from 'presentation/components';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {UploadRefundFiles} from 'presentation/pages/private/refund/components';
import {translations} from 'presentation/translations-new';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {UploadModals} from 'presentation/pages/private/refund/components/UploadModals';
import {useUploadRefundFile} from 'presentation/pages/private/refund/components/UploadRefundFiles/hooks/useUploadRefundFile';
import {useRequestPreviewDataStore} from '../../store/useRequestPreviewDataStore';
import {RequestPreviewSummary} from '../RequestPreviewSummary';
import {StyledFooterContainer, StyledStack} from './styles';
import {UploadDocumentsProps} from './types';

const {previewRequestLabel, documentsAlert} = translations['pt-br'].privateFlow.previewRefund;
const {advance} = translations['pt-br'].privateFlow.refund.additionalDocuments.sendDocuments;

const {error} = translations['pt-br'].others.uploadFiles;

export function UploadDocuments({handleNextStep}: UploadDocumentsProps) {
	const {aboveMobile} = useMediaQueries();
	const requestPreviewData = useRequestPreviewDataStore(state => state.data);
	const setRequestPreviewData = useRequestPreviewDataStore(state => state.setData);
	const [sendingAttachState, setSendingAttachState] = useState<boolean>(false);
	const [errorMessageState, setErrorMessageState] = useState<string | null>(null);

	const {
		uploadedFiles,
		deleteFile,
		handleUpload,
		IS_DELETING,
		IS_UPLOADING,
		blockCloseModal,
		IS_DELETING_ERROR,
		resetDeleteError,
	} = useUploadRefundFile({
		protocolCode: Number(requestPreviewData.protocolCode),
		requestCode: Number(requestPreviewData?.requestCode),
		postFactory: makePostUploadFilePreview,
		deleteFactory: makeDeleteFilePreview,
		clearErrorMessage: () => setErrorMessageState(null),
	});

	const someFileIsUploaded = uploadedFiles?.some(file => file.uploaded === true);

	const handleGoToReview = () => {
		if (IS_UPLOADING) {
			setSendingAttachState(true);
			return;
		}

		if (!someFileIsUploaded) {
			setErrorMessageState(error);
			return;
		}

		setRequestPreviewData({...requestPreviewData, files: uploadedFiles});
		handleNextStep();
	};

	return (
		<Fragment>
			<Container>
				<Stack mt={aboveMobile ? 4 : 2} gap={2}>
					{requestPreviewData ? (
						<AccordionData
							title={previewRequestLabel}
							subtitle={`Nº ${requestPreviewData.request}`}
							disableGutters>
							<StyledStack>
								<RequestPreviewSummary />
							</StyledStack>
						</AccordionData>
					) : null}
					<Stack gap={2}>
						<AlertInfo.Root severity="info">
							<AlertTitle sx={{fontWeight: 400, fontSize: 14, margin: 0}}>
								{documentsAlert}
							</AlertTitle>
						</AlertInfo.Root>
						<UploadRefundFiles
							removeFile={deleteFile}
							shouldShowTermsModal={{
								show: false,
							}}
							onChangeFile={handleUpload}
							values={uploadedFiles}
							isUploading={IS_UPLOADING}
							externalError={errorMessageState}
							setExternaErrorMessage={setErrorMessageState}
						/>
					</Stack>

					<Stack direction="row" justifyContent="flex-end">
						<CTAButtonFixed.Root>
							<CTAButtonFixed.Action
								data-tag-id={
									RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_REVISAO_PREVIA_REEMBOLSO
								}
								size="large"
								onClick={handleGoToReview}>
								{advance}
							</CTAButtonFixed.Action>
						</CTAButtonFixed.Root>
					</Stack>
				</Stack>
			</Container>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>

			<UploadModals
				openErrorModal={IS_DELETING_ERROR}
				closeErrorModa={resetDeleteError}
				openSendingAttachModal={sendingAttachState}
				closeSendingAttachModal={() => setSendingAttachState(false)}
				openPendingDialog={IS_DELETING}
				closePendingDialog={blockCloseModal}
			/>
		</Fragment>
	);
}
